import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowDropDown, AssignmentOutlined } from "@mui/icons-material";
import { useMemo, useState } from "react";
import InputComment from "./InputComment";
import { User } from "./UserItem";
import { useTranslations } from "hooks";
import { formatDate, getTranslation } from "common/utilities";
import AvatarRem from "./AvatarRem";

export interface Comment {
  comment: string;
}
export interface CompleteComment extends Comment {
  idprocess: number;
  idflownode: number;
  flownodetype: number;
  iduser: number;
  user: User;
  username: string;
  startdoing: string;
  done: string;
  state: number;
  previous: number;
  idcomment: number;
  creation: string;
  idprocessmodel: number;
  activityname: string;
  designerid: string;
  idprocesstoken: number;
  idswimlane: number;
  id: number;
  can_edit: boolean;
  edited: boolean;
}
export interface CommentSectionProps {
  comments?: CompleteComment[];
  canWrite?: boolean;
  canEdit?: boolean;
  onComment?: (comment: Comment) => void;
  onEdit?: (id: number, comment: string) => void;
}

/**
 * Group comments by the activity (flownode) they were posted on.
 * Falls back to a synthetic "Senza attività" group when `activityname` is missing
 * (defensive: legacy rows or unscheduled comments).
 */
type CommentGroup = {
  activityId: number | string;
  activityName: string;
  items: CompleteComment[];
};

const groupCommentsByActivity = (comments: CompleteComment[]): CommentGroup[] => {
  const map = new Map<string, CommentGroup>();
  for (const c of comments) {
    const key = `${c.idflownode ?? "unknown"}::${c.activityname ?? ""}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(c);
    } else {
      map.set(key, {
        activityId: c.idflownode ?? key,
        activityName: c.activityname || "Senza attività",
        items: [c],
      });
    }
  }
  // Sort items within each group by creation descending (newest first)
  const allGroups = Array.from(map.values());
  allGroups.forEach((group) => {
    group.items.sort((a: CompleteComment, b: CompleteComment) => {
      const ta = a.creation ? new Date(a.creation).getTime() : 0;
      const tb = b.creation ? new Date(b.creation).getTime() : 0;
      return tb - ta;
    });
  });
  // Sort groups: most recent comment wins (descending by max creation)
  allGroups.sort((a, b) => {
    const ta = a.items[0]?.creation ? new Date(a.items[0].creation).getTime() : 0;
    const tb = b.items[0]?.creation ? new Date(b.items[0].creation).getTime() : 0;
    return tb - ta;
  });
  return allGroups;
};

const CommentsSection: React.FC<CommentSectionProps> = ({
  comments,
  canWrite,
  canEdit = true,
  onComment,
  onEdit,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [comment, setComment] = useState<string>("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const translations = useTranslations();

  const avatarStyles = {
    width: 32,
    height: 32,
    fontSize: 16,
  };

  const groups = useMemo<CommentGroup[]>(
    () => (comments && comments.length ? groupCommentsByActivity(comments) : []),
    [comments]
  );

  // MD3: palette deterministica per id utente.
  // Ogni entry ha una variante per light e una per dark mode (color + onColor).
  // main = colore bubble, container = variante tenue per header.
  type ChatTone = { main: string; on: string; container: string; onContainer: string };
  // Tinte tenui in stile "WhatsApp": la bolla ha una sfumatura
  // appena percettibile del colore utente, non un pieno saturo.
  // I `main` sono usati solo come accento (nome utente, bottone Edit).
  const chatPalette: ChatTone[] = [
    // Blu (primary-like)
    { main: "#1976D2", on: "#FFFFFF", container: "rgba(25, 118, 210, 0.12)", onContainer: "#0B3D91" },
    // Verde
    { main: "#2E7D32", on: "#FFFFFF", container: "rgba(46, 125, 50, 0.12)", onContainer: "#0F3D11" },
    // Arancione
    { main: "#ED6C02", on: "#FFFFFF", container: "rgba(237, 108, 2, 0.14)", onContainer: "#7A3E00" },
    // Viola
    { main: "#7B1FA2", on: "#FFFFFF", container: "rgba(123, 31, 162, 0.12)", onContainer: "#4A0072" },
    // Teal
    { main: "#00897B", on: "#FFFFFF", container: "rgba(0, 137, 123, 0.12)", onContainer: "#003D33" },
    // Rosa
    { main: "#C2185B", on: "#FFFFFF", container: "rgba(194, 24, 91, 0.12)", onContainer: "#6A0036" },
    // Indaco
    { main: "#3949AB", on: "#FFFFFF", container: "rgba(57, 73, 171, 0.12)", onContainer: "#1A237E" },
    // Marrone/ambra
    { main: "#A16207", on: "#FFFFFF", container: "rgba(161, 98, 7, 0.14)", onContainer: "#5A3700" },
  ];
  const darkPalette: ChatTone[] = [
    { main: "#90CAF9", on: "#0B1F3A", container: "rgba(144, 202, 249, 0.18)", onContainer: "#CFE3FF" },
    { main: "#A5D6A7", on: "#0F2A11", container: "rgba(165, 214, 167, 0.18)", onContainer: "#CFEBD2" },
    { main: "#FFB74D", on: "#3A1F00", container: "rgba(255, 183, 77, 0.18)", onContainer: "#FFE6C2" },
    { main: "#CE93D8", on: "#2A0F3D", container: "rgba(206, 147, 216, 0.18)", onContainer: "#F0D9F7" },
    { main: "#80CBC4", on: "#0F2A28", container: "rgba(128, 203, 196, 0.18)", onContainer: "#B2DFDB" },
    { main: "#F48FB1", on: "#3A0F22", container: "rgba(244, 143, 177, 0.18)", onContainer: "#F8BBD0" },
    { main: "#9FA8DA", on: "#0F1A4A", container: "rgba(159, 168, 218, 0.18)", onContainer: "#C5CAE9" },
    { main: "#FCD34D", on: "#3A2A00", container: "rgba(252, 211, 77, 0.18)", onContainer: "#FDE68A" },
  ];

  const toneForUser = (id: number | undefined, dark: boolean): ChatTone => {
    const palette = dark ? darkPalette : chatPalette;
    if (id == null) return palette[0];
    return palette[Math.abs(id) % palette.length];
  };

  const renderComment = (c: CompleteComment) => {
    const { comment: commentText, user, creation, id, can_edit, edited } = c;
    const tone = toneForUser(user?.id, isDark);

    return (
      <Stack
        key={`comment-${id}`}
        id={`comment-${id}`}
        alignItems="flex-start"
        direction="row"
        spacing={1.5}
        sx={{ width: "100%" }}
      >
        <AvatarRem icon={user?.picture || ""} sx={avatarStyles}>
          {user?.firstname?.[0]}
          {user?.lastname?.[0]}
        </AvatarRem>
        <Stack flex={1} spacing={0.5} direction="column" sx={{ minWidth: 0 }}>
          {/* Header: nome + data/ora + edit (stile MD3, surface tones) */}
          <Stack
            alignItems="center"
            direction="row"
            spacing={1}
            sx={{ px: 0.5 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: tone.onContainer, fontSize: 13 }}
            >
              {user?.firstname} {user?.lastname}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 12 }}>
              {formatDate(creation)}
            </Typography>
            {edited && (
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontStyle: "italic",
                  color: "text.disabled",
                }}
              >
                · {getTranslation(translations, "generic.edited") || "modificato"}
              </Box>
            )}
            {canEdit && can_edit && (
              <Button
                size="small"
                variant="text"
                sx={{
                  minWidth: 0,
                  p: 0,
                  ml: 0.5,
                  fontSize: 12,
                  textTransform: "none",
                  color: tone.main,
                }}
                onClick={() => {
                  setEditingId(id);
                  setEditValue(commentText);
                }}
              >
                Edit
              </Button>
            )}
          </Stack>

          {/* Bubble: Box semplice (no Paper/elevation) per evitare doppi sfondi */}
          <Box
            onClick={
              canEdit && can_edit && editingId !== id
                ? () => {
                    setEditingId(id);
                    setEditValue(commentText);
                  }
                : undefined
            }
            sx={{
              cursor: canEdit && can_edit && editingId !== id ? "pointer" : "default",
              // MD3 shape: angolo piccolo in alto-sinistra = coda chat
              borderRadius: "4px 16px 16px 16px",
              // Pastel/tenue: usiamo la variante `container` della
              // palette utente come sfondo e `onContainer` come colore
              // del testo, così le bolle restano leggibili e
              // visivamente rilassate.
              bgcolor: tone.container,
              color: tone.onContainer,
              px: 1.75,
              py: 1.25,
              display: "inline-block",
              maxWidth: "100%",
              transition: "filter 120ms ease",
              "&:hover": {
                filter: isDark ? "brightness(1.08)" : "brightness(0.97)",
              },
            }}
          >
            {editingId === id ? (
              <InputComment
                value={editValue}
                onChange={setEditValue}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && editValue) {
                    e.preventDefault();
                    onEdit && onEdit(id, editValue);
                    setEditingId(null);
                  }
                }}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    onEdit && onEdit(id, editValue);
                    setEditingId(null);
                  }
                }}
              />
            ) : (
              <InputComment
                value={commentText}
                readonly
                key={`${id}-${commentText}`}
              />
            )}
          </Box>
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack direction="column" spacing={1.5}>
      {canWrite && (
        <Stack alignItems="center" direction="row" spacing={1}>
          <Box flex={1} >
            <Stack direction="column" spacing={1}>
              <InputComment
                value={comment}
                onChange={setComment}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && comment) {
                    e.preventDefault();
                    onComment && onComment({ comment });
                    setComment("");
                  }
                }}
              />
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  id="post-comment-button"
                  color="primary"
                  variant="contained"
                  size="small"
                  disabled={!comment}
                  onClick={() => {
                    onComment && onComment({ comment });
                    setComment("");
                  }}
                >
                  {getTranslation(translations, "generic.send.caption")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      )}

      {groups.length > 0 ? (
        groups.map((group) => (
          <Box key={`group-${group.activityId}`} sx={{ mt: 2 }}>
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              // Disable the default MUI Collapse animation (which makes
              // the panel "leak" upward toward the send button when it
              // opens). We render the body directly with extra inner
              // padding so there is no visual jump.
              TransitionProps={{ timeout: 0 }}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                "&:before": { display: "none" },
                boxShadow: "none",
                "&.Mui-expanded": { margin: 0 },
                "& .MuiCollapse-wrapper": { transition: "none" },
                "& .MuiCollapse-root": { transition: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ArrowDropDown />}
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(84, 189, 193, 0.12)"
                      : "rgba(84, 189, 193, 0.08)",
                  borderLeft: "4px solid",
                  borderLeftColor: "primary.main",
                  minHeight: 44,
                  "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center" },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flex: 1 }}
                >
                  <AssignmentOutlined sx={{ color: "primary.main", fontSize: 18 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, flex: 1, color: "text.primary" }}
                  >
                    {group.activityName}
                  </Typography>
                  <Chip
                    label={group.items.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  p: 2,
                  // A clear visual separation between the summary header
                  // and the comment bubbles so the panel never looks
                  // glued to the send button above.
                  borderTop: "1px solid",
                  borderTopColor: "divider",
                }}
              >
                <Stack direction="column" spacing={1.5}>
                  {group.items.map(renderComment)}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        ))
      ) : (
        // Fallback: if no grouped comments (e.g. empty list), keep previous flat behavior
        // is unnecessary because the parent already shows an empty state. Render nothing.
        null
      )}
    </Stack>
  );
};

export default CommentsSection;



